import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { SectionHeading, Reveal } from "./ui";
import ProjectCard from "./ProjectCard";
import Lightbox from "./Lightbox";
import CostCalculator from "./CostCalculator";
import { Search, Compass } from "lucide-react";

const WIDTHS = ["Any", "15", "20", "25", "30", "40", "50", "60"];
const FACINGS = ["Any", "North", "South", "East", "West", "North-East", "North-West", "South-East", "South-West"];
const FLOORS = ["Any", "1", "2", "3", "4"];
const CATS = ["Any", "Residential", "Commercial", "Interior", "Renovation"];

export default function FindDesign() {
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ width: "Any", facing: "Any", floors: "Any", category: "Any" });
  const [applied, setApplied] = useState(null);
  const [lb, setLb] = useState({ items: [], index: null });

  useEffect(() => { api.get("/projects?limit=200").then((r) => setProjects(r.data.items || [])); }, []);

  const results = useMemo(() => {
    const q = applied || f;
    return projects.filter((p) => {
      if (q.width !== "Any") {
        const w = (p.plot_width || (p.plot_size || "").split(/[x×]/)[0] || "").replace(/\D/g, "");
        if (w !== q.width) return false;
      }
      if (q.facing !== "Any" && (p.facing || "") !== q.facing) return false;
      if (q.floors !== "Any" && String(p.floors || "") !== q.floors) return false;
      if (q.category !== "Any" && p.category !== q.category && !(p.tags || []).includes(q.category)) return false;
      return true;
    });
  }, [projects, applied, f]);

  const sel = "w-full rounded-xl border border-line bg-white px-4 py-3 text-[15px] outline-none focus:border-gold";
  const openLb = (i) => setLb({ items: results.map((p) => ({ url: p.cover_image, name: p.name, category: p.tags?.[0], slug: p.slug })), index: i });

  return (
    <section className="py-16 md:py-20 bg-sand" id="find">
      <div className="container-x">
        <Reveal><SectionHeading center kicker="Free Tools" title="Plan Your Project with GNONS" subtitle="Find a matching design or estimate your construction cost — two quick tools to get you started." /></Reveal>

        <div className="grid lg:grid-cols-2 gap-6 mt-10 items-stretch">
          {/* Find Your House Design */}
          <Reveal className="rounded-xl2 bg-white border border-line p-5 md:p-6 h-full flex flex-col" data-testid="find-design-panel">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center"><Compass size={20} className="text-gold" /></span>
              <h3 className="text-lg font-bold text-ink">Find Your House Design</h3>
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="find-filters">
              <div><label className="text-xs font-semibold text-ink">Plot Width (ft)</label><select className={sel + " mt-1.5"} value={f.width} onChange={(e) => setF({ ...f, width: e.target.value })} data-testid="find-width">{WIDTHS.map((x) => <option key={x}>{x}</option>)}</select></div>
              <div><label className="text-xs font-semibold text-ink">Facing</label><select className={sel + " mt-1.5"} value={f.facing} onChange={(e) => setF({ ...f, facing: e.target.value })} data-testid="find-facing">{FACINGS.map((x) => <option key={x}>{x}</option>)}</select></div>
              <div><label className="text-xs font-semibold text-ink">Floors</label><select className={sel + " mt-1.5"} value={f.floors} onChange={(e) => setF({ ...f, floors: e.target.value })} data-testid="find-floors">{FLOORS.map((x) => <option key={x}>{x}</option>)}</select></div>
              <div><label className="text-xs font-semibold text-ink">Type</label><select className={sel + " mt-1.5"} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="find-category">{CATS.map((x) => <option key={x}>{x}</option>)}</select></div>
            </div>
            <button onClick={() => setApplied({ ...f })} data-testid="find-submit" className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-gold text-white font-semibold py-3 shadow-gold hover:bg-gold-dark transition-colors">
              <Search size={16} /> Find Designs
            </button>
            <p className="mt-3 text-xs text-muted" data-testid="find-count">{results.length} matching design{results.length !== 1 ? "s" : ""}</p>
          </Reveal>

          {/* Construction Cost Calculator */}
          <Reveal><CostCalculator /></Reveal>
        </div>

        <div className="mt-8">
          {results.length === 0 ? (
            <p className="text-center text-muted py-10">No matching designs. Try widening your filters or contact us for a custom design.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-testid="find-results">
              {results.slice(0, 8).map((p, i) => <ProjectCard key={p.id} project={p} onOpen={() => openLb(i)} aspect="aspect-[4/5]" />)}
            </div>
          )}
        </div>
      </div>
      <Lightbox items={lb.items} index={lb.index} onClose={() => setLb({ items: [], index: null })} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />
    </section>
  );
}
